import React, { ChangeEvent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { useTranslation } from 'react-i18next';
import { loadStripe, Stripe, StripeCardElement, StripeCardElementChangeEvent, TokenResult } from '@stripe/stripe-js';

import {
  CreditCard,
  CreditCardService,
  Plan,
  PlanService,
  UserVoucherService,
} from 'us-web-services';
import { Button, ButtonSize, ButtonType } from '../Button';
import { colors } from '../constants';
import { PaywallProps } from './PaywallProps';

interface Props {
  createPlan(): Promise<void>;
  creditCard?: CreditCard;
  handleError(e: unknown): void;
  loadPlans(): Promise<void>;
  paywallProps: PaywallProps;
  selectedPlan?: Plan;
  setCreditCard(creditCard: CreditCard): void;
  setErrorMessage(errorMessage: string): void;
  setShowPaymentScreen(showScreen: boolean): void;
  setSuccessMessage(successMessage: string): void;
}

export const FamilyPaywallPayment = ({
  createPlan,
  creditCard,
  handleError,
  loadPlans,
  paywallProps,
  selectedPlan,
  setCreditCard,
  setErrorMessage,
  setShowPaymentScreen,
  setSuccessMessage,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { onSuccess, paymentOnly, recaptchaKey, stripeKey, userId } = paywallProps;

  const [stripe, setStripe] = useState<Stripe>();
  const [cardElement, setCardElement] = useState<StripeCardElement>();
  const [cardFilled, setCardFilled] = useState(false);
  const [removingCard, setRemovingCard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherSubmitting, setVoucherSubmitting] = useState(false);
  type CardType = 'default' | 'american-express' | 'discover' | 'master-card' | 'visa';

  const cardLabels: Record<CardType, string> = {
    'default': 'Card Number',
    'american-express': 'American Express Card',
    'discover': 'Discover Card',
    'master-card': 'Mastercard',
    'visa': 'Visa Card',
  };

  const getCardLabel = (type: string): string => {
    const validType = type.toLowerCase() as CardType;
    return cardLabels[validType] || cardLabels.default;
  };

  const getCta = (): string => paymentOnly
    ? t('components.modals.parentPaywall.cta.addPaymentMethod')
    : t('components.modals.parentPaywall.cta.upgrade');

  const getDiscount = (): number => (selectedPlan?.discount ? Number(selectedPlan.discount) : 0);

  const getMsaDiscount = (): number => (selectedPlan?.masterServiceAreaDiscount ? Number(selectedPlan.masterServiceAreaDiscount) : 0);

  const formatMoney = (value: number): string => value.toFixed(2);

  const getTotal = (fee: number): number => {
    const total = fee - getDiscount() - getMsaDiscount();
    return Math.max(Number(total.toFixed(2)), 0);
  }

  const createCreditCardAndPlan = async (data: CreditCard): Promise<void> => {
    try {
      const response = (await CreditCardService.create(data)).data;

      setCreditCard(response);

      if (paymentOnly) {
        onSuccess();
      } else {
        await createPlan();
      }
    } catch (e) {
      handleError(e);
      setSubmitting(false);
    }
  }

  const handlePaymentToken = (result: TokenResult): void => {
    if (result?.token) {
      const data: CreditCard = {
        paymentToken: result.token.id,
        userId,
      };

      window.grecaptcha.enterprise.execute(recaptchaKey, { action: 'creditcard' }).then(
        async (token: string): Promise<void> => {
          data.recaptchaToken = token;
          await createCreditCardAndPlan(data);
        }
      );
    } else {
      setErrorMessage(result?.error?.message || '');
      setSubmitting(false);
    }
  }

  const createPaymentCard = async (): Promise<void> => {
    if (!stripe || !cardElement) {
      setErrorMessage(t('components.modals.parentPaywall.genericError'));
      setSubmitting(false);

      return;
    }

    try {
      const response = await stripe.createToken(cardElement);

      handlePaymentToken(response);
    } catch (e) {
      handleError(e);
      setSubmitting(false);
    }
  }

  const needsPaymentInfo = (): boolean => !creditCard?.id;

  const disableSubmit = (): boolean => {
    if (submitting) {
      return true;
    }

    if (needsPaymentInfo()) {
      return !(termsAccepted && cardFilled);
    }

    return !termsAccepted;
  }

  const submitPayment = async (): Promise<void> => {
    setSubmitting(true);
    if (needsPaymentInfo()) {
      await createPaymentCard();
    } else {
      await createPlan();
    }
  }

  const disableVoucherApply = (): boolean => !voucherCode || voucherSubmitting
  const handleVoucherChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setVoucherCode(event.target.value);
  }

  const redeemVoucher = async (): Promise<void> => {
    const voucher = {
      voucher: {
        code: voucherCode,
      },
      userId,
    };

    try {
      const prevDiscount = getDiscount();
      setVoucherSubmitting(true);
      await UserVoucherService.create(voucher);

      setVoucherCode('');
      await loadPlans();

      if (selectedPlan?.id) {
        try {
          const response = (await PlanService.getByFilter({
            params: {
              fields: 'discount',
              parentId: userId,
              userId,
            },
          })).data;
          const refreshedPlan = (response.data || []).find((p: Plan) => p.id === selectedPlan.id);
          const newDiscount = refreshedPlan?.discount ? Number(refreshedPlan.discount) : 0;
          if (newDiscount > prevDiscount) {
            setSuccessMessage(t('components.modals.parentPaywall.payment.voucherAppliedSuccess'));
          } else {
            setSuccessMessage(t('components.modals.parentPaywall.payment.voucherSavedNotApplied'));
          }
        } catch {
          // TODO: This seems wrong, likely cause is the PlanService.get failing...
          setSuccessMessage(t('components.modals.parentPaywall.payment.voucherSuccess'));
        }
      } else {
        setSuccessMessage(t('components.modals.parentPaywall.payment.voucherSuccess'));
      }
    } catch (e) {
      handleError(e);
    } finally {
      setVoucherSubmitting(false);
    }
  }

  const onCardChange = (event: StripeCardElementChangeEvent): void => {
    setCardFilled(event.complete);
  }

  const initStripeCreditCard = async (): Promise<void> => {
    const style = {
      base: {
        color: colors.almostBlack.DEFAULT,
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: colors.gray['400'],
        },
      },
    };

    const stripePromise = await loadStripe(stripeKey);
    if (!stripePromise) {
      setErrorMessage(t('components.modals.parentPaywall.genericError'));
      return;
    }

    const stripeCardElement = stripePromise?.elements().create('card', { style });
    setStripe(stripePromise);
    setCardElement(stripeCardElement);

    stripeCardElement.on('change', onCardChange);
    setTimeout((): void => {
      const cardContainer = document.getElementById('family-card-element');

      if (cardContainer) {
        stripeCardElement.mount('#family-card-element');
      }
    });
  };

  const handleCheckbox = (): void => {
    setTermsAccepted(!termsAccepted);
  };

  useEffect(() => {
    if (needsPaymentInfo()) {
      initStripeCreditCard();
    }
  }, []);

  const getDiscountCode = (): string => selectedPlan && selectedPlan.discountCode ?
    selectedPlan.discountCode : t('components.modals.parentPaywall.payment.discountApplied');

  const getAppliedVoucherCount = (): number => {
    const list = selectedPlan?.voucherDiscounts;
    return Array.isArray(list) ? list.length : 0;
  };

  const getDiscountLabel = (): string => {
    const name = getDiscountCode();
    const count = getAppliedVoucherCount();

    const remaining = Math.max(count - 1, 0);
    return remaining > 0
      ? t('components.modals.parentPaywall.payment.voucherLabelMultiple', { name, remaining })
      : t('components.modals.parentPaywall.payment.voucherLabelSingle', { name });
  };

  const removeCreditCard = async (): Promise<void> => {
    if (!removingCard) {
      setRemovingCard(true);
      await CreditCardService.delete(creditCard?.id || 0);
      setCreditCard({});
      setRemovingCard(false);
      initStripeCreditCard();
    }
  }

  return (
    <>
      {!paymentOnly && (
      <div className='bg-gray-50 p-4'>
        <h3 className='mb-4'>{t('components.modals.parentPaywall.payment.header')}</h3>
        <div className='mb-4'>
          {t('components.modals.parentPaywall.payment.membership')} &nbsp;
          <button type='button'
                  className='text-sapphire cursor-pointer p-0 border-none bg-transparent'
                  onClick={(): void => setShowPaymentScreen(false)}>
            ({t('components.modals.parentPaywall.payment.change')})
          </button>
          <b className='float-right'>${selectedPlan?.fee != null ? formatMoney(Number(selectedPlan.fee)) : ''}</b>
        </div>
        {selectedPlan?.masterServiceAreaDiscount && (
        <div className='mb-4'>
          {t('components.modals.parentPaywall.payment.todayOnlyDiscount')}
          <b className='float-right'>-${formatMoney(getMsaDiscount())}</b>
        </div> )}
        <div className='flex items-center justify-between gap-1 sm:gap-4 w-full mb-4'>
          <div className='whitespace-nowrap'>
            {t('components.modals.parentPaywall.payment.voucherApplyLabel')}
          </div>
          <div className='flex-1' />
          <div className='flex gap-2 w-fit shrink-0'>
            <input
              id='voucher-input'
              onChange={handleVoucherChange}
              value={voucherCode}
              maxLength={255}
              type='text'
              placeholder={t('components.modals.parentPaywall.payment.voucherApplyPlaceholder')}
              className='w-40'
              disabled={voucherSubmitting}
            />
            <Button
              buttonType={ButtonType.secondary}
              className='px-5 ml-1 lg:ml-2'
              disabled={disableVoucherApply()}
              onClick={redeemVoucher}
              buttonSize={ButtonSize.small}
            >
              {!voucherSubmitting && t('components.modals.parentPaywall.payment.voucherApplyButton')}
              {voucherSubmitting && (<span><i className='fa-solid fa-spinner fa-spin' /></span>)}
            </Button>
          </div>
        </div>
        {selectedPlan?.discount != null && selectedPlan?.discount > 0 && (
        <div className='mb-4'>
          {getDiscountLabel()}<b className='float-right'>-${formatMoney(getDiscount())}</b>
        </div> )}
        {selectedPlan?.fee && (
        <div>
          <b>{t('components.modals.parentPaywall.payment.total')}</b> <b
          className='float-right'>${formatMoney(getTotal(selectedPlan?.fee))}</b>
        </div> )}
      </div> )}
      {needsPaymentInfo() && (
      <div>
        <h3 className='mt-4'>{t('components.modals.parentPaywall.payment.cardDetails')}</h3>
        <div className='float-right text-right pad-v-5'>
          <span className='text-sm font-light color-quinary mr-2'>
            {t('components.modals.parentPaywall.payment.securedByStripe')}
            <FontAwesomeIcon icon={faLock} className='ml-2' />
          </span>
        </div>
        <div className='pad-h-15 padding-t-15 mb-4 border'>
          <form id='family-paywall-payment-form'>
            <div className='py-3'>
              <div id='family-card-element' />
            </div>
          </form>
        </div>
      </div> )}
      {!needsPaymentInfo() && (
      <div className='border-2 border-sapphire-50 p-4 mt-4'>
        {getCardLabel(creditCard?.type || '')}: {creditCard?.number}
          {!removingCard && <button type='button' className='float-right cursor-pointer p-0 border-none bg-transparent'
              onClick={removeCreditCard}>X</button>}
          {removingCard && <button type='button' className='float-right cursor-pointer p-0 border-none bg-transparent'>
            <i className='fa-solid fa-spinner fa-spin' /> <span>Loading...</span>
          </button>}
      </div> )}
      <div className='mt-4'>
        <div className='mb-4 flex items-start'>
          <input
            id='terms-checkbox'
            className='cursor-pointer rounded text-sapphire mt-3 mr-2'
            type='checkbox'
            checked={termsAccepted}
            onChange={handleCheckbox} />
          <label className='text-xs font-normal cursor-pointer' htmlFor='terms-checkbox'>
            {t('components.modals.parentPaywall.payment.termsAgreement')}
          </label>
        </div>
        <div className='mb-4'>
          <a href='/terms-of-service#section-8'
             className='font-semibold text-sm text-sapphire'
             target='_blank'>{t('components.modals.parentPaywall.payment.paymentPolicies')}</a>
        </div>
      </div>
      <div className='text-center space-x-4 mt-4'>
        <Button buttonSize={ButtonSize.wide} buttonType={ButtonType.primary} disabled={disableSubmit()} onClick={submitPayment}>
          {getCta()}
        </Button>
      </div>
    </>
  );
};
